import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, File as FileIcon, Layers, Package } from 'lucide-react';

/**
 * File Tree Component for hierarchical data display
 * Supports folders (categories) and files (subcategories/items)
 */

const TreeItem = ({ item, level = 0, onItemClick, selectedId, expandedIds, onToggle }) => {
  const isExpanded = expandedIds.has(item.id);
  const isSelected = selectedId === item.id;
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      onToggle(item.id);
    }
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const Icon = item.icon || (hasChildren ? (isExpanded ? FolderOpen : Folder) : FileIcon);
  const indentStyle = {
    paddingLeft: `${level * 16 + 12}px`
  };

  return (
    <div className="select-none">
      <div
        className={`
          group flex items-center px-3 py-2 cursor-pointer transition-colors
          ${isSelected
            ? 'bg-brand-50 text-brand-700 border-l-2 border-brand-600'
            : 'hover:bg-steel-50 border-l-2 border-transparent'
          }
        `}
        style={indentStyle}
        onClick={handleClick}
      >
        {hasChildren && (
          <span className="mr-1 flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-slate-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-500" />
            )}
          </span>
        )}
        <Icon
          className={`h-4 w-4 mr-2 flex-shrink-0 ${
            isSelected ? 'text-brand-600' : 'text-slate-600 group-hover:text-slate-700'
          }`}
        />
        <span className={`
          text-sm truncate ${
            isSelected ? 'font-medium text-brand-700' : 'text-slate-700 group-hover:text-slate-900'
          }
        `}>
          {item.label}
        </span>
        {item.count !== undefined && (
          <span className={`
            ml-auto text-xs px-1.5 py-0.5 rounded-lg ${
              isSelected
                ? 'bg-brand-100 text-brand-700'
                : 'bg-steel-100 text-slate-600 group-hover:bg-steel-200'
            }
          `}>
            {item.count}
          </span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {item.children.map(child => (
            <TreeItem
              key={child.id}
              item={child}
              level={level + 1}
              onItemClick={onItemClick}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FileTree = ({
  data,
  onItemClick,
  selectedId,
  initialExpandedIds = [],
  className = ''
}) => {
  const [expandedIds, setExpandedIds] = useState(new Set(initialExpandedIds));

  const handleToggle = (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  return (
    <div className={`bg-white ${className}`}>
      {data.map(item => (
        <TreeItem
          key={item.id}
          item={item}
          level={0}
          onItemClick={onItemClick}
          selectedId={selectedId}
          expandedIds={expandedIds}
          onToggle={handleToggle}
        />
      ))}
    </div>
  );
};

export default FileTree;
